import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api, type ProductRow, type ProductsQuery } from '../api';

const PAGE_SIZE = 100;

type Filters = Omit<ProductsQuery, 'limit' | 'offset'>;

const EMPTY_FILTERS: Filters = {};

export function ProductsPage() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Filters>({});

  const offset = (page - 1) * PAGE_SIZE;

  const filterValues = useQuery({ queryKey: ['products-filters'], queryFn: api.productFilters, staleTime: 60_000 });
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['products', filters, page],
    queryFn: () => api.products({ ...filters, limit: PAGE_SIZE, offset }),
    placeholderData: keepPreviousData,
  });

  const updateFilter = <K extends keyof Filters>(k: K, v: Filters[K]) => {
    setFilters((f) => ({ ...f, [k]: v }));
    setPage(1);
  };

  const resetFilters = () => { setFilters(EMPTY_FILTERS); setPage(1); };
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const items = data?.items ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const firstShown = data && items.length > 0 ? offset + 1 : 0;
  const lastShown = data ? offset + items.length : 0;

  return (
    <>
      <div className="page-header">
        <h2>Товары</h2>
        <input
          className="search"
          placeholder="Поиск по названию или артикулу"
          value={filters.q ?? ''}
          onChange={(e) => updateFilter('q', e.target.value || undefined)}
        />
      </div>

      <div className="card" style={{ marginBottom: 12, padding: 12 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <FilterSelect
            label="Категория"
            value={filters.category}
            options={filterValues.data?.categories.map((c) => ({ value: c.value, label: `${c.value} (${c.count})` })) ?? []}
            onChange={(v) => updateFilter('category', v)}
          />
          <FilterSelect
            label="Бренд"
            value={filters.brand}
            options={filterValues.data?.brands.map((b) => ({ value: b.value, label: `${b.value} (${b.count})` })) ?? []}
            onChange={(v) => updateFilter('brand', v)}
          />
          <FilterSelect
            label="Остатки"
            value={filters.stock}
            options={[
              { value: 'in', label: '✓ В наличии' },
              { value: 'out', label: '○ Нет в наличии' },
            ]}
            onChange={(v) => updateFilter('stock', v as Filters['stock'])}
          />
          <FilterSelect
            label="Индекс Ozon"
            value={filters.colorIndex}
            options={[
              { value: 'GREEN', label: '🟢 GREEN (выгодная)' },
              { value: 'YELLOW', label: '🟡 YELLOW' },
              { value: 'RED', label: '🔴 RED (дороже рынка)' },
              { value: 'WITHOUT_INDEX', label: '⚪ Без индекса' },
            ]}
            onChange={(v) => updateFilter('colorIndex', v as Filters['colorIndex'])}
          />
          <FilterSelect
            label="РРЦ"
            value={filters.rrp}
            options={[
              { value: 'set', label: '✓ Есть РРЦ' },
              { value: 'missing', label: '○ Без РРЦ' },
              { value: 'violation', label: '⚠ Нарушение' },
            ]}
            onChange={(v) => updateFilter('rrp', v as Filters['rrp'])}
          />
          <FilterSelect
            label="Статус"
            value={filters.visible}
            options={[
              { value: 'true', label: 'Показываются' },
              { value: 'false', label: 'Скрытые' },
            ]}
            onChange={(v) => updateFilter('visible', v as Filters['visible'])}
          />
          {activeFilterCount > 0 && (
            <button className="btn" onClick={resetFilters} style={{ marginLeft: 'auto' }}>
              ✕ Сбросить ({activeFilterCount})
            </button>
          )}
        </div>
      </div>

      {isLoading && <div className="card">Загрузка…</div>}
      {error && <div className="card" style={{ color: 'var(--red)' }}>{String((error as Error).message)}</div>}

      {data && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div className="muted">
              Всего: <strong>{data.total}</strong> · Показано {firstShown}–{lastShown}
              {isFetching && ' · обновление…'}
            </div>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>

          <table className="products">
            <thead>
              <tr>
                <th style={{ width: 60 }}></th>
                <th>Название</th>
                <th>Артикул / SKU</th>
                <th className="num">Цена</th>
                <th className="num">Маркет.</th>
                <th>Индекс</th>
                <th className="num" title="Минимальная цена на других площадках (Wildberries, Я.Маркет и др.)">Min внешн.</th>
                <th className="num" title="Минимальная цена среди других продавцов Ozon на этот же товар">Min Ozon</th>
                <th className="num" title="Минимальная цена по вашим же карточкам на других маркетплейсах">Min МП</th>
                <th className="num" title="Рекомендованная розничная цена из 1С">РРЦ</th>
                <th className="num">Остаток</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => <ProductRow key={p.productId} p={p} />)}
              {items.length === 0 && (
                <tr><td colSpan={11} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                  {activeFilterCount > 0 ? 'Нет товаров под текущие фильтры.' : 'Каталог пуст. Запустите синк товаров в разделе «Синхронизация».'}
                </td></tr>
              )}
            </tbody>
          </table>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>

          <div className="muted" style={{ fontSize: 12, marginTop: 8, maxWidth: 800 }}>
            <strong>Цены конкурентов:</strong> Ozon API не отдаёт сырые цены продавцов, только агрегированные минимумы. «Min внешн.» — на других маркетплейсах (WB, ЯМ), «Min Ozon» — среди других продавцов Ozon.
          </div>
        </>
      )}
    </>
  );
}

interface OptionItem { value: string; label: string }

function FilterSelect({ label, value, options, onChange }: {
  label: string;
  value: string | undefined;
  options: OptionItem[];
  onChange: (v: string | undefined) => void;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', fontSize: 11, color: 'var(--text-muted)', gap: 2 }}>
      <span>{label}</span>
      <select
        className="input"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || undefined)}
        style={{ minWidth: 160 }}
      >
        <option value="">— все —</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function Pagination({ page, totalPages, onChange }: { page: number; totalPages: number; onChange: (p: number) => void }) {
  if (totalPages <= 1) return null;
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      <button className="btn" disabled={page <= 1} onClick={() => onChange(1)}>« 1</button>
      <button className="btn" disabled={page <= 1} onClick={() => onChange(page - 1)}>‹</button>
      <span style={{ fontSize: 13, padding: '0 8px' }}>стр. <strong>{page}</strong> из {totalPages}</span>
      <button className="btn" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>›</button>
      <button className="btn" disabled={page >= totalPages} onClick={() => onChange(totalPages)}>{totalPages} »</button>
    </div>
  );
}

function ProductRow({ p }: { p: ProductRow }) {
  return (
    <tr>
      <td>{p.image ? <img src={p.image} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} /> : null}</td>
      <td>
        <div style={{ fontWeight: 500 }}>{p.name}</div>
        <div className="muted" style={{ fontSize: 12 }}>{p.brand}{p.category && ` · ${p.category}`}</div>
      </td>
      <td>
        <div>{p.offerId}</div>
        <div className="muted" style={{ fontSize: 12 }}>{p.sku ?? '—'}</div>
      </td>
      <td className="num">
        {p.price ? `${p.price} ₽` : '—'}
        {p.oldPrice && <div className="muted" style={{ fontSize: 12, textDecoration: 'line-through' }}>{p.oldPrice} ₽</div>}
      </td>
      <td className="num">{p.marketingPrice ? `${p.marketingPrice} ₽` : '—'}</td>
      <td>
        {p.colorIndex ? (
          <>
            <span className={`color-dot ${p.colorIndex}`} />
            <span className="muted" style={{ fontSize: 12 }}>{p.colorIndex}</span>
          </>
        ) : <span className="muted">—</span>}
      </td>
      <td className="num">{fmtMin(p.extMinPrice, p.price)}</td>
      <td className="num">{fmtMin(p.ozonMinPrice, p.price)}</td>
      <td className="num">{fmtMin(p.mpMinPrice, p.price)}</td>
      <td className="num">
        {p.rrpPrice ? (
          <>
            <div>{Number(p.rrpPrice).toFixed(0)} ₽</div>
            {p.rrpViolation && (
              <div style={{ fontSize: 11, color: 'var(--red)', fontWeight: 600 }} title={`Эффективная клиентская: ${p.rrpViolation.effective} ₽, ниже РРЦ на ${(p.rrpViolation.gapPct * 100).toFixed(1)}%`}>
                ⚠ {p.rrpViolation.effective} ₽ ({(p.rrpViolation.gapPct * 100).toFixed(0)}% ниже)
              </div>
            )}
          </>
        ) : <span className="muted">—</span>}
      </td>
      <td className="num">{p.stockTotal}</td>
    </tr>
  );
}

function fmtMin(competitor: string | null, ours: string | null): React.ReactNode {
  if (!competitor || competitor === '0' || competitor === '0.00') return <span className="muted">—</span>;
  const c = Number(competitor);
  const o = ours ? Number(ours) : null;
  if (!Number.isFinite(c) || c <= 0) return <span className="muted">—</span>;
  const color = o == null ? undefined : c < o ? 'var(--red)' : c > o ? 'var(--green)' : undefined;
  return <span style={{ color }}>{c.toFixed(0)} ₽</span>;
}
